import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ValueContrast } from "@/components/ValueContrast";
import { LibraryMosaic } from "@/components/LibraryMosaic";
import { SaleNote } from "@/components/SaleNote";
import { BuyButton, AnchorButton } from "@/components/Buttons";
import { TEACHER_PLANS, TEACHER_LIBRARY, countItems, fmt } from "@/data/offer";

const seats = (id) => (id === "teacher1" ? "1 konto" : `${id === "teacher5" ? "5" : "10"} kont dla nauczycieli`);

const PlanDetails = ({ plan, total }) => (
  <div data-testid={`teacher-plan-panel-${plan.id}`} className="border border-ink/15 bg-white p-7 sm:p-10">
    <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
      <ValueContrast value={plan.value} price={plan.price} testId={`teacher-contrast-${plan.id}`} />
      <div className="flex flex-col justify-between gap-8">
        <ul className="space-y-3 text-[15px] text-ink/70">
          {[`Cała biblioteka nauczycielska: ${total} pozycji`, seats(plan.id), "Dostęp przez 12 miesięcy", "Certyfikaty uczestnictwa po kursach"].map((t) => (
            <li key={t} className="flex gap-3">
              <span className="mt-[9px] h-1.5 w-1.5 shrink-0 bg-red" />
              {t}
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-3">
          <BuyButton href={plan.url} testId={`teacher-buy-${plan.id}-button`}>
            Wybieram ten abonament
          </BuyButton>
          <SaleNote className="!mt-0" />
          <AnchorButton to="#co-dostajesz" variant="outline" testId={`teacher-library-link-${plan.id}`}>
            Zobacz, co jest w bibliotece
          </AnchorButton>
        </div>
      </div>
    </div>
  </div>
);

export const TeacherPlans = () => {
  const [active, setActive] = useState(null);
  const total = countItems(TEACHER_LIBRARY);

  return (
    <section id="nauczycielki" data-testid="teacher-plans-section" className="bg-alabaster py-20 sm:py-28">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow>Abonament nauczycielski</Eyebrow>
            <h2 className="font-display mt-5 text-4xl font-medium leading-[1.02] tracking-[-0.02em] text-ink sm:text-5xl lg:text-6xl">
              Abonament dla nauczycielek i&nbsp;nauczycieli
            </h2>
          </div>
          <p className="self-end text-base leading-relaxed text-ink/65 lg:col-span-5 sm:text-lg">
            Biblioteka {total} szkoleń, kursów, kursoksiążek i e-booków rozwijających warsztat nauczyciela języka obcego:
            od metodyki i testowania po asertywność, kreatywność i planowanie czasu.
          </p>
        </Reveal>

        <div className="mt-14">
          <LibraryMosaic
            items={TEACHER_LIBRARY.flatMap((g) => g.items)}
            caption={`${total} produktów w jednej bibliotece nauczycielskiej`}
            testId="teacher-library-mosaic"
          />
        </div>

        <Reveal className="mt-16">
          <div className="border-t border-ink/15">
            {TEACHER_PLANS.filter((p) => p.id === "teacher1").map((p) => {
              const on = p.id === active;
              return (
                <div key={p.id} data-testid={`teacher-plan-row-${p.id}`} className="relative border-b border-ink/15">
                  <span className={`absolute left-0 top-0 h-full w-[3px] origin-top bg-red transition-transform duration-300 ${on ? "scale-y-100" : "scale-y-0"}`} />
                  <div className="grid grid-cols-1 gap-5 py-7 pl-5 sm:grid-cols-12 sm:items-center sm:gap-6">
                    <div className="sm:col-span-6">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-red">{p.audience}</span>
                      <h3 className="font-display mt-1.5 text-2xl leading-tight text-ink sm:text-3xl">{p.label}</h3>
                      <p className="mt-2 text-[14px] leading-relaxed text-ink/55">{p.note}</p>
                    </div>
                    <div className="sm:col-span-3 sm:text-right">
                      <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/45">Wartość osobno</span>
                      <span
                        data-testid={`teacher-plan-row-value-${p.id}`}
                        className="font-display mt-1 block text-3xl leading-none text-ink/60 line-through decoration-red decoration-[3px] sm:text-4xl"
                      >
                        {fmt(p.value)}
                      </span>
                      <span className="mt-3 block text-[10px] font-semibold uppercase tracking-[0.18em] text-red">Abonament</span>
                      <span className="font-display mt-1 block text-3xl leading-none text-red sm:text-4xl">
                        {fmt(p.price)}
                        <span className="block font-sans text-[11px] font-medium text-ink/50">/ rok</span>
                      </span>
                    </div>
                    <div className="sm:col-span-3 sm:text-right">
                      <button
                        type="button"
                        aria-expanded={on}
                        aria-controls={`teacher-plan-panel-${p.id}`}
                        onClick={() => setActive(on ? null : p.id)}
                        data-testid={`teacher-plan-tab-${p.id}`}
                        className={`group inline-flex w-full items-center justify-center gap-2 border px-5 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 sm:w-auto ${
                          on ? "border-ink bg-ink text-white" : "border-ink/30 text-ink hover:border-ink hover:bg-ink hover:text-white"
                        }`}
                      >
                        {on ? "Zwiń szczegóły" : "Poznaj szczegóły"}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${on ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {on && (
                      <motion.div
                        id={`teacher-plan-panel-${p.id}`}
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-8 pl-5">
                          <PlanDetails plan={p} total={total} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal className="mt-12">
          <div data-testid="teacher-team-contact" className="flex flex-col gap-4 border border-ink/15 bg-white px-7 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-9">
            <p className="font-display text-2xl font-medium leading-tight text-ink sm:text-3xl">
              Potrzebujesz dostępu dla większej liczby nauczycieli?
            </p>
            <a
              href="mailto:info@luizawojtowiczwaga.com"
              data-testid="teacher-team-contact-link"
              className="inline-flex items-center gap-2 text-[15px] font-semibold text-red underline-offset-4 hover:underline"
            >
              Napisz do mnie: info@luizawojtowiczwaga.com
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
